import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { User } from '../models/user.model';
import { Page } from '../models/page.model';
import { RoleService } from './role.service';

@Injectable({ providedIn: 'root' })
export class UserService {
  private usersUrl = '/users';

  private me: User;

  constructor(
    private http: HttpClient,
    private roleService: RoleService,
  ) {}

  getUsers(organisationId: number, roles: string[]): Observable<User[]> {
    let q = '';
    if (organisationId) {
      q = '?organisation=' + organisationId;
    }
    if (roles && roles.length > 0) {
      if (q) {
        q += '&';
      } else {
        q = '?';
      }
      q += 'roles=' + roles.join(',');
    }

    return this.http.get<any>(this.usersUrl + '/all' + q).pipe(
      map((response) => response.data),
      catchError(this.handleError),
    );
  }

  getUser(id: number): Observable<User> {
    return this.http.get<any>(this.usersUrl + '/' + id).pipe(
      map((response) => response.data),
      catchError(this.handleError),
    );
  }

  getMe(): Observable<User> {
    if (this.me) {
      return of(this.me);
    }

    return this.http.get<any>('/me').pipe(
      map((response) => {
        this.me = response.data;
        return this.me;
      }),
      catchError(this.handleError),
    );
  }

  getUsersAdvanced(
    pageIndex: number,
    itemsPerPage: number,
    sortColumnName: string,
    sortColumnOrder: string,
    filter: any,
    filterOrga: string,
  ): Observable<Page<User>> {
    let url = this.usersUrl + '?size=' + itemsPerPage + '&page=' + (pageIndex - 1);
    if (filter) {
      if (filter.filter) {
        const filterNew = encodeURIComponent(filter.filter.trim());

        url += '&search=' + filterNew;
      }
      if (filter.filterAdvisor && filter.filterAdvisor != 'null') {
        url += '&advisor=' + encodeURIComponent(filter.filterAdvisor);
      }
      if (filter.roles && filter.roles.length) {
        url += '&roles=' + filter.roles.join(',');
      }
    }
    if (filterOrga) {
      url += '&orga=' + encodeURIComponent(filterOrga);
    }
    if (sortColumnName) {
      url += '&sort=' + sortColumnName + ',' + sortColumnOrder;
    }
    return this.http.get<any>(url).pipe(
      map((response) => response.data),
      catchError(this.handleError),
    );
  }

  save(user: User): Observable<User> {
    if (user.id) {
      return this.put(user);
    }
    return this.post(user);
  }

  delete(user: User) {
    const url = `${this.usersUrl}/${user.id}`;

    return this.http.delete(url).pipe(catchError(this.handleError));
  }

  private handleError(error: any) {
    console.error('An error occurred', error);
    return throwError(() => error.message || error);
  }

  // Add new user
  private post(user: User): Observable<User> {
    return this.http.post<any>(this.usersUrl, user).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  // Update existing user
  private put(user: User) {
    const url = `${this.usersUrl}/${user.id}`;

    return this.http.put<any>(url, user).pipe(
      map(() => user),
      catchError(this.handleError),
    );
  }
}
